import { observer } from 'owa-mobx-react';
import { otherViewFilterText } from 'owa-locstrings/lib/strings/otherviewfiltertext.locstring.json';
import { focusedViewFilterText } from 'owa-locstrings/lib/strings/focusedviewfiltertext.locstring.json';
import loc from 'owa-localize';
import React, { useMemo, useCallback } from 'react';
import { useState } from 'owa-react';
import Droppable from 'owa-dnd/lib/components/Droppable';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { selectFocusedViewFilter } from 'owa-mail-triage-table-utils';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { Tab, TabList, type SelectTabData, type SelectTabEvent } from '@fluentui/react-components';
import type { MailboxInfo } from 'owa-client-types';
import { CopilotResultsTab } from '../../lazyFunctions';
import {
    getFocusedOtherDropViewState,
    getFocusedFilterForTable,
    getStore as getListViewStore,
    isCopilotResultsTable,
} from 'owa-mail-list-store';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';

import {
    full,
    medium,
    compact,
    tabItemContainer,
    dropContainer,
    dropHoverTreatment,
    tabListRoot,
    tabListContainer,
    tab,
    tabText,
    tab_Win32Style,
    tabTextSelected_Win32Style,
    tabTextUnselected,
    tabTextUnselectedHover,
    tabTextUnselected_Win32Style,
    tabText_Win32Style,
} from './InboxPivotStyles.scss';

import classnames from 'owa-classnames';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';

export interface FocusedInboxPivotProps {
    tableViewId: string;
    onDropFocused: (dragInfo: DragData) => void;
    onDropOther: (dragInfo: DragData) => void;
    onKeyDown: (ev: React.KeyboardEvent<HTMLElement>, tableViewId: string) => void;
    isSupportedItemTypeForDragDrop: boolean;
    shouldShowOnDropHoverTreatment: boolean;
    showCopilotResultsTab?: boolean;
    mailboxInfo?: MailboxInfo;
}

interface DroppableTabProps {
    tabKey: string;
    label: string;
    onDrop: (dragInfo: DragData) => void;
    selectedKey: string;
    isSupportedItemTypeForDragDrop: boolean;
    shouldShowOnDropHoverTreatment: boolean;
    densityModeCssClass: string;
    tabContentProps: {
        className: string;
    };
    unselectedTabContentProps: {
        className: string;
    };
    isWin32Style: boolean;
}

const DroppableTab = observer(function DroppableTab({
    tabKey,
    label,
    onDrop,
    selectedKey,
    isSupportedItemTypeForDragDrop,
    shouldShowOnDropHoverTreatment,
    densityModeCssClass,
    tabContentProps,
    unselectedTabContentProps,
    isWin32Style,
}: DroppableTabProps) {
    const [isHovered, setIsHovered] = useState(false);
    const isDropTarget = tabKey !== selectedKey && isSupportedItemTypeForDragDrop;
    const droppableStyles = classnames(
        tabItemContainer,
        isDropTarget && dropContainer,
        shouldShowOnDropHoverTreatment && dropHoverTreatment,
        densityModeCssClass
    );

    const tabContentPropsToUse =
        selectedKey === tabKey ? tabContentProps : unselectedTabContentProps;
    const tabContentWithHover = useMemo(
        () => ({
            ...tabContentPropsToUse,
            className: classnames(
                tabContentPropsToUse.className,
                isHovered && !isWin32Style && selectedKey !== tabKey && tabTextUnselectedHover
            ),
        }),
        [tabContentPropsToUse, isHovered, selectedKey, isWin32Style]
    );

    const handleMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleMouseLeave = useCallback(() => setIsHovered(false), []);

    return (
        <Droppable
            classNames={droppableStyles}
            dropViewState={getFocusedOtherDropViewState()}
            onDrop={onDrop}
        >
            <Tab
                value={tabKey}
                className={classnames(tab, isWin32Style && tab_Win32Style)}
                content={tabContentWithHover}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {label}
            </Tab>
        </Droppable>
    );
},
'DroppableTab');

const defaultExport: React.FunctionComponent<FocusedInboxPivotProps> = observer(
    function FocusedInboxPivot(props: FocusedInboxPivotProps) {
        const isWin32Style = isWin32StylingEnabled();
        const tableView = getListViewStore().tableViews.get(props.tableViewId);
        const isTypeRampEnabled = useIsTypeRampUpdatesEnabled();
        const densityModeCssClass = isTypeRampEnabled
            ? getDensityModeCssClass(full, medium, medium)
            : getDensityModeCssClass(full, medium, compact);

        const baseTabContentClassName = useComputedValue(
            () => ({
                className: classnames(tabText, densityModeCssClass),
            }),
            [densityModeCssClass]
        );
        const tabContentProps = useComputedValue(
            () => ({
                className: classnames(
                    baseTabContentClassName.className,
                    isWin32Style && tabText_Win32Style,
                    isWin32Style && tabTextSelected_Win32Style
                ),
            }),
            [baseTabContentClassName, isWin32Style]
        );
        const unselectedTabContentProps = useComputedValue(
            () => ({
                className: classnames(
                    baseTabContentClassName.className,
                    isWin32Style && tabText_Win32Style,
                    isWin32Style ? tabTextUnselected_Win32Style : tabTextUnselected
                ),
            }),
            [baseTabContentClassName, isWin32Style]
        );

        const onTabSelect = useCallback((_ev: SelectTabEvent, data: SelectTabData) => {
            const key = data.value as string;
            selectFocusedViewFilter(convertPivotKeyToFocusedViewFilter(key), 'FocusedOtherPivot');
        }, []);
        /**
         * Convert the focused view filter to a string that used as the key in Pivot fabric component
         * We need to do this instead of just using the FocusedViewFilter value, because FocusedViewFilter.None(-1) is ignored by the Fabric Pivot
         */
        const getKeyForSelectedFocusedViewFilter = (): string => {
            if (isCopilotResultsTable(tableView?.tableQuery)) {
                return '';
            }
            const focusedFilterForTable = getFocusedFilterForTable(tableView);
            let selectedKey = '';
            switch (focusedFilterForTable) {
                case -1:
                    selectedKey = 'inbox';
                    break;
                case 0:
                    selectedKey = 'focused';
                    break;
                case 1:
                    selectedKey = 'other';
                    break;
            }
            return selectedKey;
        };
        /**
         * Convert pivot key string back to the focused view filter to select
         * @param key of the pivot component
         */
        const convertPivotKeyToFocusedViewFilter = (key: string) => {
            switch (key) {
                case 'focused':
                    return 0;
                case 'other':
                    return 1;
                default:
                case 'inbox':
                    return -1;
            }
        };

        const onKeyDownHandler = useCallback(
            (ev: React.KeyboardEvent<HTMLElement>) => {
                props.onKeyDown(ev, props.tableViewId);
            },
            [props.onKeyDown, props.tableViewId]
        );

        const selectedKey = getKeyForSelectedFocusedViewFilter();

        return (
            <div
                className={classnames(tabListContainer, densityModeCssClass)}
                onKeyDown={onKeyDownHandler}
            >
                <TabList
                    className={classnames(tabListRoot, densityModeCssClass)}
                    selectedValue={selectedKey}
                    onTabSelect={onTabSelect}
                >
                    <DroppableTab
                        tabKey="focused"
                        label={loc(focusedViewFilterText)}
                        onDrop={props.onDropFocused}
                        selectedKey={selectedKey}
                        isSupportedItemTypeForDragDrop={props.isSupportedItemTypeForDragDrop}
                        shouldShowOnDropHoverTreatment={props.shouldShowOnDropHoverTreatment}
                        densityModeCssClass={densityModeCssClass}
                        tabContentProps={tabContentProps}
                        unselectedTabContentProps={unselectedTabContentProps}
                        isWin32Style={isWin32Style}
                    />
                    <DroppableTab
                        tabKey="other"
                        label={loc(otherViewFilterText)}
                        onDrop={props.onDropOther}
                        selectedKey={selectedKey}
                        isSupportedItemTypeForDragDrop={props.isSupportedItemTypeForDragDrop}
                        shouldShowOnDropHoverTreatment={props.shouldShowOnDropHoverTreatment}
                        densityModeCssClass={densityModeCssClass}
                        tabContentProps={tabContentProps}
                        unselectedTabContentProps={unselectedTabContentProps}
                        isWin32Style={isWin32Style}
                    />
                    {props.showCopilotResultsTab && props.mailboxInfo && (
                        <CopilotResultsTab
                            tableViewId={props.tableViewId}
                            mailboxInfo={props.mailboxInfo}
                        />
                    )}
                </TabList>
            </div>
        );
    },
    'FocusedInboxPivot'
);
export default defaultExport;
